import React, { useEffect, useState, useRef, useLayoutEffect } from 'react';
import { motion } from 'framer-motion';
import { ClipboardListIcon, ReceiptIcon, HardHatIcon, RefreshCwIcon, SparklesIcon } from 'lucide-react';
import { Container } from '../components/Container';
const items = [{
  icon: ClipboardListIcon,
  name: 'Subjectivity collection & management',
  description: 'Chasing producers for TRIA forms, ACORD apps, underlying policies, loss history, and compliance docs.'
}, {
  icon: ReceiptIcon,
  name: 'Premium audits',
  description: 'Creating and sending audit worksheets and supporting document requests when policies expire.'
}, {
  icon: HardHatIcon,
  name: 'Inspection reports & loss control',
  description: 'Collecting inspection reports, loss control recommendations, and corrective action confirmations.'
}, {
  icon: RefreshCwIcon,
  name: 'Quote & renewal follow-ups',
  description: 'Following up with producers on outstanding quotes and upcoming renewals to keep your pipeline moving.'
}, {
  icon: SparklesIcon,
  name: 'Any paper-chasing workflow',
  description: 'Endorsements, certificates, BORs, claims documentation. If your team chases it by email today, Pigeon can automate it end to end.'
}];
const cyclingWords = ['Subjectivities.', 'Audits.', 'Inspections.', 'Renewals.', 'Follow-ups.'];
const mobileWords = [...cyclingWords, ...cyclingWords, ...cyclingWords];
const LOOP_LEN = cyclingWords.length;
export function Workflows() {
  // ----- Mobile (untouched, perfect) -----
  const mobileTrackRef = useRef<HTMLSpanElement | null>(null);
  const mobileWordRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const [mobileTick, setMobileTick] = useState(LOOP_LEN);
  const [mobileOffset, setMobileOffset] = useState(0);
  const [animateMobile, setAnimateMobile] = useState(true);
  useLayoutEffect(() => {
    const measure = () => {
      const track = mobileTrackRef.current;
      const word = mobileWordRefs.current[mobileTick];
      const container = track?.parentElement;
      if (!track || !word || !container) return;
      const containerWidth = container.offsetWidth;
      const wordCenter = word.offsetLeft + word.offsetWidth / 2;
      setMobileOffset(containerWidth / 2 - wordCenter);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [mobileTick]);
  useEffect(() => {
    if (mobileTick >= LOOP_LEN * 2) {
      const t = window.setTimeout(() => {
        setAnimateMobile(false);
        setMobileTick(v => v - LOOP_LEN);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setAnimateMobile(true));
        });
      }, 850);
      return () => window.clearTimeout(t);
    }
  }, [mobileTick]);
  useEffect(() => {
    const mql = window.matchMedia('(min-width: 1024px)');
    let timerId: number | null = null;
    const setup = () => {
      if (timerId !== null) {
        window.clearInterval(timerId);
        timerId = null;
      }
      if (!mql.matches) {
        timerId = window.setInterval(() => {
          setMobileTick(t => t + 1);
        }, 2400);
      }
    };
    setup();
    mql.addEventListener('change', setup);
    return () => {
      if (timerId !== null) window.clearInterval(timerId);
      mql.removeEventListener('change', setup);
    };
  }, []);
  // ----- Desktop: scroll-driven active row, picks the row currently crossing
  // the headline's vertical center line so each word holds for the full
  // duration of its corresponding row passing through that line. -----
  const rowRefs = useRef<(HTMLLIElement | null)[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isDesktop, setIsDesktop] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia('(min-width: 1024px)');
    const sync = () => setIsDesktop(mql.matches);
    sync();
    mql.addEventListener('change', sync);
    return () => mql.removeEventListener('change', sync);
  }, []);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia('(min-width: 1024px)');
    if (!mql.matches) return;
    let raf = 0;
    const compute = () => {
      raf = 0;
      // Aim line ~ where the sticky headline's cycling word sits (top:128px + headline center).
      const targetY = window.innerHeight * 0.32;
      const rows = rowRefs.current.filter(Boolean) as HTMLLIElement[];
      if (rows.length === 0) return;
      // Default: last row whose top is at or above the line.
      let idx = 0;
      for (let i = 0; i < rows.length; i++) {
        const r = rows[i].getBoundingClientRect();
        if (r.top <= targetY) idx = i;else break;
      }
      setActiveIndex(idx);
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(compute);
    };
    compute();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);
  return <section id="workflows" className="py-16 sm:py-24 lg:py-28" data-id="element-962">
      <Container data-id="element-963">
        <div className="grid gap-10 sm:gap-14 lg:grid-cols-[1fr_1fr] lg:gap-20" data-id="element-964">
          {/* Left: headline (sticky on desktop) */}
          <div className="lg:sticky lg:top-32 lg:self-start" data-id="element-965">
            <h2 className="display-tighten text-balance text-center font-serif-display text-[2.85rem] leading-[1.02] text-white sm:text-left sm:text-6xl sm:leading-[1.02] lg:text-[5.5rem]" data-id="element-966">
              <span className="block italic text-accent" data-id="element-967">Automate</span>

              {/* Mobile carousel (unchanged) */}
              <span className="relative block h-[1.2em] overflow-hidden [mask-image:linear-gradient(to_right,transparent_0%,black_18%,black_82%,transparent_100%)] [-webkit-mask-image:linear-gradient(to_right,transparent_0%,black_18%,black_82%,transparent_100%)] sm:hidden" data-id="element-968">
                <motion.span ref={mobileTrackRef} className="absolute top-0 flex flex-row-reverse whitespace-nowrap" animate={{
                x: mobileOffset
              }} transition={animateMobile ? {
                duration: 0.85,
                ease: [0.32, 0.72, 0, 1]
              } : {
                duration: 0
              }} data-id="element-969">
                  {mobileWords.map((w, i) => <motion.span key={i} ref={el => {
                  mobileWordRefs.current[i] = el;
                }} animate={{
                  opacity: i === mobileTick ? 1 : 0.35
                }} transition={{
                  duration: 0.5,
                  ease: 'easeOut'
                }} className="inline-block px-[0.35em] text-white" data-id="element-970">
                      {w}
                    </motion.span>)}
                </motion.span>
              </span>

              {/* Desktop: vertical stack driven by activeIndex */}
              <span className="relative hidden h-[1.15em] overflow-hidden sm:block" data-id="element-971">
                <motion.span className="absolute inset-x-0 top-0 flex flex-col" animate={{
                y: `-${activeIndex * 1.1}em`
              }} transition={{
                duration: 0.55,
                ease: [0.22, 1, 0.36, 1]
              }} data-id="element-972">
                  {cyclingWords.map(w => <span key={w} className="block h-[1.1em] text-white" data-id="element-973">
                      {w}
                    </span>)}
                </motion.span>
              </span>
            </h2>
          </div>

          {/* Right: workflow rows — flow naturally */}
          <ol className="divide-y divide-white/[0.06] border-t border-white/[0.06]" data-id="element-974">
            {items.map((it, i) => {
            const active = i === activeIndex;
            return <motion.li key={it.name} ref={el => {
              rowRefs.current[i] = el;
            }} initial={{
              opacity: 0,
              y: 10
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '400px'
            }} transition={{
              duration: 0.4,
              delay: i * 0.05
            }} className="grid grid-cols-[auto_1fr] items-start gap-5 py-6 sm:gap-8 sm:py-9" data-id="element-975">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border border-white/[0.08] bg-accent/[0.08] sm:h-12 sm:w-12" data-id="element-976">
                    <it.icon className="h-[18px] w-[18px] text-accent sm:h-5 sm:w-5" data-id="element-977" />
                  </div>
                  <div className="pt-0.5 sm:pt-1" data-id="element-978">
                    <motion.h3 animate={{
                  opacity: isDesktop ? active ? 1 : 0.55 : 1
                }} transition={{
                  duration: 0.35
                }} className="text-lg font-semibold tracking-tight text-white sm:text-2xl" data-id="element-979">
                      {it.name}
                    </motion.h3>
                    <motion.p animate={{
                  opacity: isDesktop ? active ? 1 : 0.45 : 1
                }} transition={{
                  duration: 0.35
                }} className="mt-1.5 text-sm leading-relaxed text-zinc-400 sm:mt-2 sm:text-base" data-id="element-980">
                      {it.description}
                    </motion.p>
                  </div>
                </motion.li>;
          })}
          </ol>
        </div>
      </Container>
    </section>;
}